import { IFormFieldConstraint } from '../../../../../interfaces/iform-field-constraint.interface';
import { IFormFieldConstraintLiaison } from './ifield-constraint-liason.interface';
import { FormField } from '../../../../../models/form-builder/form-field.model';
import { Form } from '../../../../../models/form-builder/form.model';
import { CachedFormFieldService, FlushCachedServiceUponFormSave } from '../../../../../services/form-field-with-cache.service';
import { ISiteFormFieldConstraints } from './isite-form-field-constraints.interface';

// Define an internally used class.
class FormFieldConstraintComparator {
    // Property.
    private fieldConstraintLiaison: IFormFieldConstraintLiaison;

    // Constructor.
    public constructor(fieldConstraintLiaison: IFormFieldConstraintLiaison) {
        this.fieldConstraintLiaison = fieldConstraintLiaison;
    }

    // Comparison method.
    public field2UsesField1Constraint(formField1: FormField, formField2: FormField): boolean {
        let usesField1Constraint: boolean = false;

        if (this.fieldConstraintLiaison.getConstraintId(formField1) == this.fieldConstraintLiaison.getConstraintId(formField2))
            usesField1Constraint = true;

        if ((!usesField1Constraint) && (formField2.childFormFields != null) && (formField2.childFormFields.length > 0)) {
            for (let index: number = 0; index < formField2.childFormFields.length; index++) {
                let formField2ChildField = formField2.childFormFields[index];

                if (formField1 != formField2ChildField) {
                    if (this.fieldConstraintLiaison.getConstraintId(formField1) == this.fieldConstraintLiaison.getConstraintId(formField2ChildField)) {
                        usesField1Constraint = true;

                        break;
                    } // if
                } // if
            } // for
        } // if

        //if ((!usesField1Constraint) && formField2.getAccessConstraintUpdate())
        if ((!usesField1Constraint) && formField2.acceptsConstraintUpdateFrom(formField1))
            usesField1Constraint = true;

        return usesField1Constraint;
    }
}

// Define a base class for common code for classes that
// manage form field constraints of a given type within a site.
export abstract class SiteConstraintsBase implements ISiteFormFieldConstraints {
    // Properties.
    private currentFieldConstraintUpdated: boolean = false;
    private constraintEditingDisabled: boolean = true;
    protected siteConstraints: IFormFieldConstraint[] = null;
    protected fieldConstraintLiaison: IFormFieldConstraintLiaison = null;
    protected constraintNameProposed: boolean = false; 

    // Constructor.
    protected constructor(fieldConstraintLiaisonParam: IFormFieldConstraintLiaison, formParam: Form, protected cachedFormFieldService: CachedFormFieldService) {
        this.fieldConstraintLiaison = fieldConstraintLiaisonParam;

        if ((formParam.eventHandler == null)) {
            let eventHandler: FlushCachedServiceUponFormSave = new FlushCachedServiceUponFormSave(cachedFormFieldService, formParam);
            formParam.eventHandler = eventHandler;
        }
    }

    // Abstract methods.
    //public abstract updateOtherFieldsUsingConstraint(form: Form, formField: FormField): void;

    // Implement some ISiteFormFieldConstraints methods.
    public constraintsLoaded(formField: FormField, response: IFormFieldConstraint[]) {
        // Save retrieved values.
        if ((response != null) && (response.length > 0)) {
            this.siteConstraints = response;
        }

        // Does this field use an existing constraint?
        let existingConstraintId: number = 0;
        let existingConstraint: IFormFieldConstraint = null;
        let formFieldConstraintId: number = this.fieldConstraintLiaison.getConstraintId(formField);
        if ((formFieldConstraintId != null) &&
            ((formFieldConstraintId > 0) || (formFieldConstraintId < 0)) &&
            (this.siteConstraints) && (this.siteConstraints.length > 0)) {
            existingConstraint = this.siteConstraints.find(c => c.id == formFieldConstraintId);
            if (existingConstraint != null) {
                existingConstraintId = existingConstraint.id;
            }
        }
        this.fieldConstraintLiaison.setConstraintId(formField, existingConstraintId);
        if (existingConstraint != null) {
            let constraintExpr: string = existingConstraint.constraintExpression;
            this.fieldConstraintLiaison.copyConstraintValueToCorrespondFormFieldProperty(formField, constraintExpr);
        }
    }

    public get ConstraintLiaison(): IFormFieldConstraintLiaison {
        return this.fieldConstraintLiaison;
    }

    public get constraintsEmpty(): boolean {
        return ((this.siteConstraints == null) || (this.siteConstraints.length == 0));
    }
    public get constraints(): IFormFieldConstraint[] {
        return this.siteConstraints;
    }

    public duplicateNameError(constraint: IFormFieldConstraint): boolean {
        let duplicateName: boolean = false;

        if (this.siteConstraints != null) {
            let otherConstraint: IFormFieldConstraint = this.siteConstraints.find(c => (c.id != constraint.id) && (c.name == constraint.name));

            if (otherConstraint != null)
                duplicateName = true;
        }

        return duplicateName;
    }

    public get editingDisabled(): boolean {
        return this.constraintEditingDisabled;
    }
    public enableEditing(): void {
        this.constraintEditingDisabled = false;
    }
    public disableEditing(): void {
        this.constraintEditingDisabled = true;
    }

    public get ConstraintNameProposed(): boolean {
        return this.constraintNameProposed;
    }
    public set ConstraintNameProposed(value: boolean) {
        this.constraintNameProposed = value;
    }

    public getId(formField: FormField): number {
        return this.fieldConstraintLiaison.getConstraintId(formField);
    }
    public setId(form: Form, formField: FormField, value: number): void {
        this.fieldConstraintLiaison.setConstraintId(formField, value);

        // Update this field's select options properties.
        if ((value > 0) || (value < 0)) {
            let constraint = 
                this.siteConstraints.find(c => c.id == value);

            if (constraint != null) {
                this.fieldConstraintLiaison.copyConstraintValueToCorrespondFormFieldProperty(formField, constraint.constraintExpression);

                this.fieldConstraintLiaison.setConstraintId(formField, constraint.id);
                this.fieldConstraintLiaison.setConstraintName(formField, constraint.name);
                this.fieldConstraintLiaison.setConstraintValue(formField, constraint.constraintExpression);
                this.fieldConstraintLiaison.setConstraintFailureMessage(formField, constraint.constraintFailureMessage);

                this.disableEditing();
            }
        } else {
            // Reset all constraint related values
            // except the displayed select options, if any.
            this.fieldConstraintLiaison.setConstraintId(formField, 0);
            this.fieldConstraintLiaison.setConstraintName(formField, null);
            this.fieldConstraintLiaison.setConstraintValue(formField, null);
            this.fieldConstraintLiaison.setConstraintFailureMessage(formField, null);

            // If the constraint value is null, also set the corresponding form field property to null.
            this.fieldConstraintLiaison.copyConstraintValueToCorrespondFormFieldProperty(formField, null);
        }
    }

    public getName(formField: FormField): string {
        return this.fieldConstraintLiaison.getConstraintName(formField);
    }
    public setName(form: Form, formField: FormField, value: string): void {
        this.fieldConstraintLiaison.setConstraintName(formField, value);

        // Update any other form fields using the same constraint.
        this.updateOtherFieldsUsingConstraint(form, formField);
    }

    public getConstraintExpression(formField: FormField): string {
        return this.fieldConstraintLiaison.getConstraintValue(formField);
    }
    public setConstraintExpression(form: Form, formField: FormField, value: string): void {
        this.fieldConstraintLiaison.setConstraintValue(formField, value);

        // Update any other form fields using the same constraint.
        this.updateOtherFieldsUsingConstraint(form, formField);
    }

    public getCurrentFieldConstraintUpdated(): boolean {
        return this.currentFieldConstraintUpdated;
    }
    public setCurrentFieldConstraintUpdated(): void {
        this.currentFieldConstraintUpdated = true;
    }

    public updateOtherFieldsUsingConstraint(form: Form, formField: FormField): void {
        // Update the constraint/form field service's copy of the constraint.
        if (this.cachedFormFieldService.cacheIsEnabled) {
            let updatedConstraint = this.fieldConstraintLiaison.packageConstraint(form.dataCollectionId, formField);
            this.fieldConstraintLiaison.updateOrCreateConstraint(this.cachedFormFieldService, updatedConstraint);

            this.fieldConstraintLiaison.setConstraintId(formField, updatedConstraint.id);
        }

        // Update any other fields using this/the same constraint.
        let formFieldComparator: FormFieldConstraintComparator = new FormFieldConstraintComparator(this.fieldConstraintLiaison);
        let otherFieldsUsingConstraint: FormField[] =
            form.formFields.filter(ff2 => ((ff2 != formField)) && formFieldComparator.field2UsesField1Constraint(formField, ff2));

        if ((otherFieldsUsingConstraint != null) && (otherFieldsUsingConstraint.length > 0)) {
            for (let index: number = 0; index < otherFieldsUsingConstraint.length; index++) {
                let otherField: FormField = otherFieldsUsingConstraint[index];

                SiteConstraintsBase.updateOtherFieldConstraintValues(form.dataCollectionId, this.fieldConstraintLiaison, formField, otherField);

                if ((otherField.childFormFields != null) && (otherField.childFormFields.length > 0)) {
                    for (let childIndex: number = 0; childIndex < otherField.childFormFields.length; childIndex++) {
                        let otherChildField: FormField = otherField.childFormFields[childIndex];

                        /*
                        if ((otherChildField != formField) &&
                            (this.fieldConstraintLiaison.getConstraintId(otherChildField) == this.fieldConstraintLiaison.getConstraintId(formField))) {
                            this.fieldConstraintLiaison.setConstraintName(otherChildField, this.fieldConstraintLiaison.getConstraintName(formField));
                            this.fieldConstraintLiaison.setConstraintValue(otherChildField, this.fieldConstraintLiaison.getConstraintValue(formField));

                            // Also update the field's constraint value(s).
                            this.fieldConstraintLiaison.copyConstraintValueToCorrespondFormFieldProperty(otherChildField, this.fieldConstraintLiaison.getConstraintValue(formField))
                        }
                        */
                        if (otherChildField != formField)
                            SiteConstraintsBase.updateOtherFieldConstraintValues(form.dataCollectionId, this.fieldConstraintLiaison, formField, otherChildField);
                    }
                }
            }
        }
    }

    // Helper methods.
    private static updateOtherFieldConstraintValues(dataCollectionId: number, fieldConstraintLiaison: IFormFieldConstraintLiaison, formField: FormField, otherField: FormField): void {
        if (fieldConstraintLiaison.getConstraintId(otherField) == fieldConstraintLiaison.getConstraintId(formField)) {
            if (fieldConstraintLiaison.getConstraintName(formField) != fieldConstraintLiaison.getConstraintName(otherField))
                fieldConstraintLiaison.setConstraintName(otherField, fieldConstraintLiaison.getConstraintName(formField));
            if (fieldConstraintLiaison.getConstraintValue(formField) != fieldConstraintLiaison.getConstraintValue(otherField))
                fieldConstraintLiaison.setConstraintValue(otherField, fieldConstraintLiaison.getConstraintValue(formField));

            // Also update the field's constraint value(s).
            if (fieldConstraintLiaison.getConstraintFormFieldPropertyValue(formField) != fieldConstraintLiaison.getConstraintFormFieldPropertyValue(otherField))
                fieldConstraintLiaison.copyConstraintValueToCorrespondFormFieldProperty(otherField, fieldConstraintLiaison.getConstraintValue(formField))
        }

        // Also call the other field's constraint updated method.
        let updatedConstraint = fieldConstraintLiaison.packageConstraint(dataCollectionId, formField);
        otherField.constraintUpdated(updatedConstraint, fieldConstraintLiaison);
    }
}
